import React, { useEffect, useRef, useState } from 'react'
import styled from '@emotion/styled';
import MenuContainer from '../../hooks/MenuContainer';
import { style_border_default } from '../../constants/stylesValues';
import MenuItemEditor from './components/MenuItemEditor';
import OptionGroupAndOptionPanel from './components/OptionGroupAndOptionPanel';
import ApplicationModel from '../../models/applications/api/ApplicationModel';
import ApplicationNlpMenuContainer from '../../hooks/ApplicationNlpMenuContainer';
import StatusIndicator from '../../components/general/StatusIndicator';
import { Prompt } from 'react-router-dom';

interface ApplicationMenuItemManagerProps {
    application: ApplicationModel
    history: any,
    orgId: string
}
const ApplicationMenuItemManager: React.FC<ApplicationMenuItemManagerProps> = (props) => {
    const menuContainer = MenuContainer.useContainer();
    const applicationNlpMenuContainer = ApplicationNlpMenuContainer.useContainer();
    const rightPanelRef = useRef(null);
    const [mobilePanel, setMobilePanel] = useState<"item" | "selection">("item");

    useEffect(() => {
        if (!props.application.manageMenuApplicationId) return;
        const url = window.location.pathname;
        if (!url) return;

        const urlItemIdMath = url.match(/\/menu\/item\/([^/]+)/)
        const itemId = Array.isArray(urlItemIdMath) ? urlItemIdMath[1] : null;

        if (!menuContainer.categories || menuContainer.categories.length === 0 && !menuContainer?.menuItems?.length) {
            menuContainer.getMenu(props.application.manageMenuApplicationId);
        }
        else if (!menuContainer.menuItems || menuContainer.menuItems.length === 0) {
            menuContainer.getMenu(props.application.manageMenuApplicationId);
        }

        if (menuContainer.menuItems && menuContainer.menuItems.length > 0) {
            const menuItem = menuContainer.menuItems.find(menuItem => menuItem.id === itemId);
            if (menuItem) {
                menuContainer.setMenuItem(menuItem);
                menuContainer.setOriginalMenuItem(JSON.parse(JSON.stringify(menuItem)))
            }
            else {
                props.history.goBack();
            }
        }
    }, [menuContainer.menuItems, props.application.manageMenuApplicationId, menuContainer?.menuItems?.length])


    return (
        <>
            <Prompt
                when={menuContainer.isMenuItemEdited}
                message="You have unsaved menu changes. Leave this item and discard them?"
            />
            <MobilePanelNavigation aria-label="Menu item editor sections">
                <MobilePanelButton
                    type="button"
                    isActive={mobilePanel === "item"}
                    onClick={() => setMobilePanel("item")}
                >
                    Item
                </MobilePanelButton>
                <MobilePanelButton
                    type="button"
                    isActive={mobilePanel === "selection"}
                    disabled={!menuContainer.path?.length}
                    onClick={() => setMobilePanel("selection")}
                >
                    Selection
                </MobilePanelButton>
            </MobilePanelNavigation>
            <MenuItemWrapper >
                <MenuItemEditorWrapper isMobileActive={mobilePanel === "item"}>
                    <MenuItemEditor
                        history={props.history}
                        rightPanelRef={rightPanelRef}
                        application={props.application}
                        onOpenSelection={() => setMobilePanel("selection")}
                    />
                </MenuItemEditorWrapper>

                <MenuItemOptionGroupWrapper isMobileActive={mobilePanel === "selection"}>
                    <OptionGroupAndOptionPanel
                        history={props.history}
                        application={props.application}
                        rightPanelRef={rightPanelRef}
                        orgId={props.orgId}
                    />
                </MenuItemOptionGroupWrapper>
            </MenuItemWrapper>
            {menuContainer.menuItemAugmentStatus ?
                <StatusIndicator
                    state={menuContainer.menuItemAugmentStatus}
                    failedMessage='The assistant training failed. Please try again by making any necessary updates and re-saving your menu item.'
                    loadingMessage='Training the assistant with updated menu item preferences.'
                    successMessage='The assistant is now trained with the updates to this menu item.'
                    isClosable={menuContainer.menuItemAugmentStatus === "success"}
                    onClose={() => menuContainer.setMenuItemAugmentStatus(null)}
                    onAcceptFailure={() => {
                        if (!props.application.manageMenuApplicationId) return;
                        //they have accepted the augment failure
                        //set the lastAugmentFinishTime to the start time so that the dialog is not shown
                        const menuItem = { ...menuContainer.menuItem, lastAugmentFinishTime: menuContainer.menuItem.lastAugmentStartTime };
                        applicationNlpMenuContainer.updateVoicifyMenuItem(props.application.manageMenuApplicationId, menuItem);
                        menuContainer.setMenuItemAugmentStatus(null)
                    }}
                />
                :
                <></>
            }
        </>
    )


}



const MenuItemWrapper = styled.div`
    height: 100%;
    display: flex;
    border-top: ${style_border_default};
    overflow:hidden;

    @media (max-width: 899px) {
        min-height: calc(100vh - 190px);
        overflow: visible;
    }
`;

const MenuItemEditorWrapper = styled.div<{ isMobileActive: boolean }>`
    height: var(--menu-item-workspace-height, calc(100vh - 300px));
    display: flex;
    width: 50%;
    min-width: 0;

    @media (max-width: 899px) {
        display: ${props => props.isMobileActive ? "flex" : "none"};
        width: 100%;
        height: auto;
        min-height: calc(100vh - 210px);
    }
`;

const MenuItemOptionGroupWrapper = styled.div<{ isMobileActive: boolean }>`
    border-left: ${style_border_default};
    height: 100%;
    width: 50%;
    min-width: 0;

    @media (max-width: 899px) {
        display: ${props => props.isMobileActive ? "block" : "none"};
        width: 100%;
        min-width: 0;
        border-left: none;
    }
`;

const MobilePanelNavigation = styled.nav`
    display: none;

    @media (max-width: 899px) {
        position: sticky;
        top: 0;
        z-index: 5;
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 6px;
        padding: 8px;
        border-bottom: ${style_border_default};
        background: rgba(255, 255, 255, 0.96);
    }
`;

const MobilePanelButton = styled.button<{ isActive: boolean }>`
    min-height: 44px;
    border: 1px solid ${props => props.isActive ? "rgba(49, 182, 211, 0.55)" : "rgba(29, 49, 71, 0.1)"};
    border-radius: 999px;
    background: ${props => props.isActive ? "rgba(49, 182, 211, 0.13)" : "#ffffff"};
    color: #1d3147;
    font-family: Muli, sans-serif;
    font-size: 13px;
    font-weight: 900;
    cursor: pointer;

    &:disabled {
        cursor: not-allowed;
        opacity: 0.45;
    }
`;

export default ApplicationMenuItemManager;
